// ============================================================================
// hooks/liveStreamManagment/useSourceRegistrySync.ts
// Pont entre l'ancien système (MediaStreamContext.videoSources) et le nouveau
// SourceRegistry.
//
// Ce hook est TEMPORAIRE — il disparaîtra quand la migration sera complète
// et que les sources seront créées directement dans le registry.
//
// Ce qu'il fait :
//   - Observe videoSources et remoteConnections dans MediaStreamContext
//   - Enregistre/désenregistre les sources correspondantes dans le SourceRegistry
//   - Injecte les streams des sources remote et scene
//
// Ce qu'il ne fait PAS :
//   - Il ne modifie pas l'ancien système (lecture seule sur MediaStreamContext)
//   - Il n'acquiert aucun flux (pas de getUserMedia, pas de fetch)
// ============================================================================

import { useEffect, useRef } from 'react';
import { useMediaStream, VideoSource } from '../../contexts/MediaStreamContext';
import { useSourceRegistry } from '../../contexts/liveStreamManagment/SourceRegistryContext';
import { SourceConfig } from '../../dataTypes/liveStreamManagment/sourceRegistry';

// ─── Conversion ancien type → nouveau type ────────────────────────────────────

function toSourceConfig(vs: VideoSource): SourceConfig {
  switch (vs.type) {
    case 'device':
      return {
        type: 'device',
        deviceId: vs.deviceId ?? '',
      };
case 'screen':
  return {
    type: 'screen',
    width: vs.screenConfig?.width ?? 1920,
    height: vs.screenConfig?.height ?? 1080,
    frameRate: vs.screenConfig?.frameRate ?? 30,
    cursor: vs.screenConfig?.cursor ?? 'always',
    withAudio: vs.screenConfig?.audio ?? false,
  };
case 'url':
  return {
    type     : 'hls',
    url      : vs.externalUrl ?? '',
    xtream   : vs.xtream,       // ← propager xtream depuis VideoSource
    // Le deck "Playlist" (hls-video-<ref>) est un flux auto-hébergé qui peut
    // couper brièvement côté serveur (cf. incident du 05/09/2026) : sans
    // autoRetry, le seul filet est le "Re-attach HLS" générique de
    // HlsAcquirer, qui recrée hls.js mais ne rafraîchit jamais le
    // MediaStream déjà distribué à la régie — le flux revient en coulisses,
    // la régie reste noire tant que le deck n'est pas rebasculé à la main.
    autoRetry: vs.id.startsWith('hls-video-'),
  };
    case 'remote':
      return {
        type: 'remote',
        remoteDeviceId: vs.id.replace('remote-', ''),
      };
    case 'scene':
      return {
        type: 'scene',
        layoutId: vs.id.replace('scene-', ''),
      };
    case 'jingle':
      return {
        type: 'jingle',
        url: '',
      };
    default:
      return {
        type: 'device',
        deviceId: '',
      };
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSourceRegistrySync(): void {
  const { videoSources, remoteConnections } = useMediaStream();
  const { register, unregister, injectStream, ejectStream, registry } = useSourceRegistry();

  // Garder trace des IDs déjà enregistrés pour détecter les suppressions
  const registeredIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const currentIds = new Set<string>();

    // Enregistrer ou mettre à jour chaque source
    videoSources.forEach(vs => {
      // Audio seul : aucune image à acquérir. L'enregistrer tomberait dans le cas par
      // défaut de toSourceConfig — une webcam sans deviceId, donc la caméra par défaut
      // ouverte par la première mini-vue venue. Son son passe par useAudioMetering.
      if (vs.type === 'audio') return;
      currentIds.add(vs.id);

      register({
        id:     vs.id,
        label:  vs.label,
        config: toSourceConfig(vs),
      });

      // Injecter les streams des sources remote (produits par la signalisation WebRTC)
      if (vs.type === 'remote' && vs.stream) {
		  
		console.log('[SYNC] remote', vs.id, 'stream=', vs.stream?.id, 
  'tracks=', vs.stream?.getTracks().map(t => `${t.kind}:${t.readyState}:muted=${t.muted}`).join(','),
  'existing.stream=', registry.get(vs.id)?.stream?.id ?? 'null');  
		  
        const existing = registry.get(vs.id);
        // Injecter seulement si le stream a changé ou si la source n'est pas encore active
        if (!existing?.stream || existing.stream !== vs.stream) {
          injectStream(vs.id, vs.stream);
        }
      }

      // Injecter les streams des scènes (produits par le ScenePipelineManager)
      if (vs.type === 'scene' && vs.stream) {
        const existing = registry.get(vs.id);
        if (!existing?.stream || existing.stream !== vs.stream) {
          injectStream(vs.id, vs.stream);
        }
      }
    });

    // Désenregistrer les sources qui n'existent plus dans l'ancien système
    registeredIdsRef.current.forEach(id => {
      if (!currentIds.has(id)) {
        // Éjecter le stream d'abord si la source est active
        const source = registry.get(id);
        if (source?.state === 'active') {
          ejectStream(id);
        }
        unregister(id);
      }
    });

    // Mettre à jour le set de référence
    registeredIdsRef.current = currentIds;

  }, [videoSources, remoteConnections, register, unregister, injectStream, ejectStream, registry]);
}